import { useCallback, useEffect, useState } from "react";
import { spaceActions } from "redux/Ldb/actions/spaceActions";
import { useAppDispatch, useAppSelector } from "../storeHooks";
import { SpaceMessage } from "types/spaceTypes";

const useGetSpaceMessages = (
    spaceId: string,
    options: {
        skip?: boolean;
        pollingInterval?: number; // milliseconds
        refetchOnMount?: boolean;
    } = {}
): useGetSpaceMessagesProp => {
    const { skip = false, pollingInterval = 0, refetchOnMount = false } = options;
    const dispatch = useAppDispatch();
    
    // Generate a unique ID for this hook instance to track re-mounts vs re-renders
    const [hookId] = useState(() => Math.random().toString(36).substr(2, 9));

    const { fetchingSpaceMessages, fetchedSpaceMessagesSuccess, fetchedSpaceMessagesFailure } =
        useAppSelector((state) => state.space);

    const [messages, setMessages] = useState<SpaceMessage[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMoreMessages, setHasMoreMessages] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [lastRequestedPage, setLastRequestedPage] = useState(0);

    const fetchSpaceMessages = useCallback((page = 1, append = false) => {
        if (skip || !spaceId) return;

        if (append) {
            setIsLoadingMore(true);
        }

        dispatch(spaceActions.fetchSpaceMessages(spaceId, { page, limit: 50 }));
    }, [dispatch, spaceId, skip]);

    const loadMoreMessages = useCallback(() => {
        if (!hasMoreMessages || isLoadingMore || fetchingSpaceMessages) {
            return;
        }

        const nextPage = currentPage + 1;

        // Prevent duplicate requests for the same page
        if (nextPage === lastRequestedPage) {
            return;
        }

        setCurrentPage(nextPage);
        setLastRequestedPage(nextPage);
        setIsLoadingMore(true);
        fetchSpaceMessages(nextPage, true);
    }, [currentPage, hasMoreMessages, isLoadingMore, fetchingSpaceMessages, fetchSpaceMessages, lastRequestedPage]);

    const resetFetchSpaceMessages = useCallback(() => {
        dispatch(spaceActions.resetFetchSpaceMessages());
    }, [dispatch]);

    // Reset state when space changes
    useEffect(() => {
        setMessages([]);
        setCurrentPage(1);
        setHasMoreMessages(true);
        setIsLoadingMore(false);
        setLastRequestedPage(0);
    }, [spaceId]);

    // Initial fetch - only run if we don't have messages yet
    useEffect(() => {
        if (spaceId && !skip && messages.length === 0) {
            fetchSpaceMessages(1, false);
        }
    }, [spaceId, skip, fetchSpaceMessages, messages.length]);

    // Handle successful fetch
    useEffect(() => {
        if (fetchedSpaceMessagesSuccess?.data?.messages) {
            const newMessages = fetchedSpaceMessagesSuccess.data.messages;
            
            if (currentPage > 1) { // Remove isLoadingMore check - it's already false when we receive the response
                // Append older messages (backend returns newest first, so we reverse and prepend)
                const reversedMessages = [...newMessages].reverse();
                let uniqueMessageCount = 0;
                
                setMessages(prev => {
                    // Check for duplicates using _id (MongoDB style) or id
                    const existingIds = new Set(prev.map(msg => (msg as any)._id || msg.id));
                    const newUniqueMessages = reversedMessages.filter(msg => !existingIds.has((msg as any)._id || msg.id));
                    
                    uniqueMessageCount = newUniqueMessages.length; // Store count for later use

                    const combined = [...newUniqueMessages, ...prev];
                    return combined;
                });
                setIsLoadingMore(false);

                // Check if we should stop trying to load more messages
                if (newMessages.length < 50 && uniqueMessageCount > 0) {
                    // Got fewer than requested AND they were unique - end of data
                    setHasMoreMessages(false);
                } else if (uniqueMessageCount === 0 && newMessages.length > 0) {
                    // All messages were duplicates - backend pagination issue
                    // Try a few more times, but don't keep going forever
                    if (currentPage > 5) {
                        setHasMoreMessages(false);
                    }
                } else if (newMessages.length === 0) {
                    // API returned no messages - but let's check if we should try more pages
                    if (currentPage <= 3) {
                        // Don't set hasMoreMessages = false yet, try a few more pages
                    } else {
                        setHasMoreMessages(false);
                    }
                }
            } else if (currentPage === 1) {
                // Initial load only - replace all messages (reverse for chronological order)
                const reversedMessages = [...newMessages].reverse();
                setMessages(reversedMessages);
                setHasMoreMessages(newMessages.length === 50); // If we got full page, assume more exist
                setIsLoadingMore(false); // Ensure loading state is reset
            }
        }
    }, [fetchedSpaceMessagesSuccess, isLoadingMore, currentPage]);

    // Polling effect
    useEffect(() => {
        if (pollingInterval > 0 && spaceId && !skip) {
            const interval = setInterval(() => {
                fetchSpaceMessages();
            }, pollingInterval);

            return () => clearInterval(interval);
        }
    }, [pollingInterval, spaceId, skip, fetchSpaceMessages]);

    // Refetch on mount
    useEffect(() => {
        if (refetchOnMount && spaceId && !skip) {
            fetchSpaceMessages();
        }
    }, [refetchOnMount, spaceId, skip, fetchSpaceMessages]);

    return {
        fetchSpaceMessages,
        isSuccess: Boolean(fetchedSpaceMessagesSuccess),
        isError: Boolean(fetchedSpaceMessagesFailure),
        isLoading: fetchingSpaceMessages,
        data: messages,
        error: fetchedSpaceMessagesFailure,
        resetFetchSpaceMessages,
        refetch: () => fetchSpaceMessages(1, false),
        loadMoreMessages,
        hasMoreMessages,
        isLoadingMore,
    };
};

type useGetSpaceMessagesProp = {
    fetchSpaceMessages: (page?: number, append?: boolean) => void;
    isSuccess: boolean;
    isError: boolean;
    isLoading: boolean;
    data: SpaceMessage[];
    error: any;
    resetFetchSpaceMessages: () => void;
    refetch: () => void;
    loadMoreMessages: () => void;
    hasMoreMessages: boolean;
    isLoadingMore: boolean;
};

export default useGetSpaceMessages;